/**
 * Main Calculator component - orchestrates the debt calculation workflow
 * Constitutional requirement: React with regular JavaScript, console logging
 */

import { useState, useRef } from 'react'
import { 
  X, 
  Trash2, 
  BarChart3, 
  Calendar, 
  Calculator as CalculatorIcon, 
  TrendingUp, 
  Hand, 
  Lightbulb, 
  Lock 
} from 'lucide-react'
import InputForm from './InputForm.jsx'
import PaymentTable from './PaymentTable.jsx'
import ResultsDisplay from './ResultsDisplay.jsx'
import { DebtCalculationInput } from '../models/DebtModels.js'
import { calculateDebtPayoff, calculateRequiredPayment } from '../services/debtCalculator.js'

const Calculator = () => {
 
  // State management
  const [isCalculating, setIsCalculating] = useState(false)
  const [calculationResult, setCalculationResult] = useState(null)
  const [activeTab, setActiveTab] = useState('results') // 'results', 'schedule'
  const [error, setError] = useState(null)

  // Ref for auto-scrolling to results on mobile/tablet
  const resultsRef = useRef(null)

  // Handle calculation request from InputForm
  const handleCalculate = async (inputData) => {
    console.log('🚀 Starting debt calculation with input:', inputData)
    console.log('📊 Calculation mode:', inputData.calculationMode)
    
    setIsCalculating(true)
    setError(null)
    
    try {
      let result

      if (inputData.calculationMode === 'payoffTime') {
        // Payoff time calculation (existing functionality)
        console.log('⏰ Calculating payoff time...')
        
        const calculationInput = new DebtCalculationInput(
          inputData.balance,
          inputData.interestRate,
          inputData.monthlyPayment,
          'payoffTime'
        )

        result = calculateDebtPayoff(calculationInput)
        
      } else if (inputData.calculationMode === 'monthlyPayment') {
        // Required monthly payment calculation (new functionality)
        console.log('💳 Calculating required monthly payment...')
        
        result = calculateRequiredPayment(
          inputData.balance,
          inputData.interestRate,
          inputData.targetMonths
        )
        
      } else {
        throw new Error(`Invalid calculation mode: ${inputData.calculationMode}`)
      }
      
      console.log('✅ Debt calculation completed successfully')
      console.log('📊 Result summary:', result.summary)
      
      setCalculationResult(result)
      
      // Set active tab to results
      setActiveTab('results')
      
      // Auto-scroll to results on mobile/tablet after state update
      setTimeout(() => {
        if (resultsRef.current) {
          console.log('📱 Auto-scrolling to results section for mobile/tablet view')
          resultsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          })
        }
      }, 100) // Small delay to ensure DOM updates
      
      //console.log('💾 Calculation result updated in state (stateless mode)')
      
    } catch (error) {
      console.log('❌ Error during debt calculation:', error)
      setError(`Calculation failed: ${error.message}`)
    } finally {
      setIsCalculating(false)
    }
  }

  // Handle tab change
  const handleTabChange = (tab) => {
    console.log(`📑 Switching to tab: ${tab}`)
    setActiveTab(tab)
  }

  // Clear all data
  const handleClearAll = () => {
    //console.log('🗑️ Clearing all calculator data')
    setCalculationResult(null)
    setError(null)
    setActiveTab('results')
  }

  // Error display component
  const ErrorDisplay = () => (
    <div className="error-display">
      <div className="error-message">
        <span className="error-icon">
          <X size={16} color="#ff6b35" />
        </span>
        <span className="error-text">{error}</span>
        <button 
          className="error-dismiss" 
          onClick={() => setError(null)}
          title="Dismiss error"
        >
          <X size={14} color="#666" />
        </button>
      </div>
    </div>
  )

  //console.log('🎨 Rendering Calculator with calculation result:', !!calculationResult)

  return (
    <div className="calculator">

      {/* Error Display */}
      {error && <ErrorDisplay />}

      {/* Main Content Layout */}
      <div className="calculator-layout">
        
        {/* Input Section */}
        <section className="input-section">
          <InputForm 
            onCalculate={handleCalculate}
            isCalculating={isCalculating}
          />
        </section>

        {/* Results Section */}
        <section className="results-section" ref={resultsRef}>
          
          {calculationResult ? (
            <>
              {/* Enhanced Tab Navigation */}
              <nav className="tab-navigation enhanced">
                <button
                  className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
                  onClick={() => handleTabChange('results')}
                >
                  <div className="tab-icon-container">
                    <BarChart3 size={16} color="#ff6b35" />
                    {activeTab !== 'results' && (
                      <span className="tab-pulse-dot"></span>
                    )}
                  </div>
                  <div className="tab-text">
                    <span>Results & Charts</span>
                    <small>3 interactive visuals</small>
                  </div>
                </button>
                <button
                  className={`tab-button ${activeTab === 'schedule' ? 'active' : ''}`}
                  onClick={() => handleTabChange('schedule')}
                >
                  <div className="tab-icon-container">
                    <Calendar size={16} color="#ff6b35" />
                    {activeTab !== 'schedule' && (
                      <span className="tab-pulse-dot"></span>
                    )}
                  </div>
                  <div className="tab-text">
                    <span>Payment Schedule</span>
                    <small>{calculationResult?.paymentSchedule?.length || 0} payments</small>
                  </div>
                </button>
              </nav>

              {/* Tab Content */}
              <div className="tab-content">
                
                {activeTab === 'results' && (
                  <>
                    <ResultsDisplay 
                      calculationResult={calculationResult}
                    />
                    {/* Call-to-Action for Payment Schedule */}
                    <div className="tab-cta">
                      <div className="cta-content">
                        <Calendar size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
                        <span>Want to see your month-by-month breakdown? Check out the </span>
                        <button 
                          className="cta-link" 
                          onClick={() => handleTabChange('schedule')}
                        >
                          Payment Schedule →
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'schedule' && (
                  <>
                    <PaymentTable 
                      paymentSchedule={calculationResult.paymentSchedule}
                      summary={calculationResult.summary}
                    />
                    {/* Call-to-Action for Results & Charts */}
                    <div className="tab-cta">
                      <div className="cta-content">
                        <BarChart3 size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
                        <span>See visual charts and insights in </span>
                        <button 
                          className="cta-link" 
                          onClick={() => handleTabChange('results')}
                        >
                          Results & Charts →
                        </button>
                      </div>
                    </div>
                  </>
                )}

              </div>
            </>
          ) : (
            /* Welcome State */
            <div className="welcome-state">
              <div className="welcome-content">
                <h2>
                  <Hand size={24} color="#ff6b35" style={{display: 'inline', marginRight: '8px'}} />
                  Welcome to the Debt Calculator
                </h2>
                <div className="welcome-features">
                  <div className="feature">
                    <span className="feature-icon">
                      <BarChart3 size={24} color="#ff6b35" />
                    </span>
                    <div className="feature-content">
                      <h3>Visual Charts & Analytics</h3>
                      <p>See your balance reduction over time and payment breakdowns with interactive charts</p>
                    </div>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">
                      <Calendar size={24} color="#ff6b35" />
                    </span>
                    <div className="feature-content">
                      <h3>Detailed Payment Schedule</h3>
                      <p>View month-by-month payment breakdowns showing interest vs principal</p>
                    </div>
                  </div>
                </div>
                <div className="get-started enhanced">
                  <p>
                    <TrendingUp size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
                    <strong>Get started:</strong> Enter your credit card details in the form to calculate your payoff timeline!
                  </p>
                  <div className="exploration-hint">
                    <span className="hint-icon">💡</span>
                    <span>After calculating, explore both the <strong>visual charts</strong> and <strong>detailed schedule</strong> tabs for complete insights!</span>
                  </div>
                </div>
              </div>
            </div>
          )}

        </section>
      </div>

      {/* Footer */}
      <footer className="calculator-footer">
        <div className="footer-content">
          <p>
            <Lightbulb size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            <strong>Tip:</strong> Increasing your monthly payment can significantly reduce total interest paid and payoff time.
          </p>
          <div className="footer-links">
            <span className="footer-note">
              <Lock size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
              All calculations are performed locally - your data never leaves your browser.
            </span>
          </div>
        </div>
      </footer>

    </div>
  )
}

export default Calculator
